import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateQuoteInput, UpdateQuoteInput } from '@/lib/marina/validators'

export type QuoteStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED' | 'CONVERTED'

export interface QuoteLineItem {
  id: string
  description: string
  quantity: string
  unitPriceIdr: string
  amountIdr: string
  sortOrder: number
}

export interface Quote {
  id: string
  code: string
  customerId: string
  customer?: {
    id: string
    firstName: string
    lastName: string
    email: string | null
    category: string
  }
  boatId: string | null
  boat?: { id: string; name: string } | null
  bookingId: string | null
  booking?: { id: string; code: string } | null
  issueDate: string
  validUntil: string | null
  subtotalIdr: string
  taxRate: string
  taxIdr: string
  totalIdr: string
  currency: string
  fxRateToBase: string | null
  status: QuoteStatus
  notes: string | null
  lineItems?: QuoteLineItem[]
  invoice?: { id: string; code: string } | null
  createdAt: string
  updatedAt: string
}

export interface QuotesListResponse {
  items: Quote[]
  total: number
  page: number
  pageSize: number
}

export interface QuotesListParams {
  q?: string
  status?: QuoteStatus
  customerId?: string
  page?: number
  pageSize?: number
  sort?: string
  order?: 'asc' | 'desc'
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function jsonFetch<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

function toQuery(params: Record<string, unknown>): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export function useQuotes(params: QuotesListParams = {}) {
  return useQuery({
    queryKey: ['quotes', params],
    queryFn: () => apiFetch<QuotesListResponse>(`/api/quotes${toQuery(params as Record<string, unknown>)}`),
    staleTime: 15_000,
  })
}

export function useQuoteStatusCounts() {
  return useQuery({
    queryKey: ['quotes', 'status-counts'],
    queryFn: () => apiFetch<{ counts: Record<string, number> }>('/api/quotes/status-counts'),
    staleTime: 15_000,
  })
}

export function useQuote(id: string | null | undefined) {
  return useQuery({
    queryKey: ['quote', id],
    queryFn: () => apiFetch<{ quote: Quote }>(`/api/quotes/${id}`),
    enabled: !!id,
  })
}

export function useCreateQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateQuoteInput) =>
      jsonFetch<{ quote: Quote }>('/api/quotes', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotes'] }),
  })
}

export function useUpdateQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateQuoteInput }) =>
      jsonFetch<{ quote: Quote }>(`/api/quotes/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['quotes'] })
      qc.invalidateQueries({ queryKey: ['quote', v.id] })
    },
  })
}

export function useDeleteQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => jsonFetch<{ ok: boolean }>(`/api/quotes/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotes'] }),
  })
}

function trans(action: 'send' | 'accept' | 'reject') {
  return (id: string) => jsonFetch<{ quote: Quote }>(`/api/quotes/${id}/${action}`, { method: 'POST', body: '{}' })
}

export function useSendQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: trans('send'),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['quotes'] })
      qc.invalidateQueries({ queryKey: ['quote', id] })
    },
  })
}
export function useAcceptQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: trans('accept'),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['quotes'] })
      qc.invalidateQueries({ queryKey: ['quote', id] })
    },
  })
}
export function useRejectQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: trans('reject'),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['quotes'] })
      qc.invalidateQueries({ queryKey: ['quote', id] })
    },
  })
}

export function useConvertQuoteToInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dueDate }: { id: string; dueDate: string }) =>
      jsonFetch<{ invoice: { id: string; code: string } }>(`/api/quotes/${id}/convert-to-invoice`, {
        method: 'POST',
        body: JSON.stringify({ dueDate }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['quotes'] })
      qc.invalidateQueries({ queryKey: ['quote', vars.id] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}
